import * as React from 'react';
import { StrictMode } from 'react';
import { BrowserRouter } from 'react-router-dom';
// REDUX
import { Provider } from 'react-redux';
import { store } from './redux/store';
// React Query
import { QueryClient, QueryCache, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
// MATERIAL UI
import { ThemeProvider, Theme, StyledEngineProvider } from '@mui/material/styles';
import theme from './styles/muiTheme';
// HELMET
import { HelmetProvider } from 'react-helmet-async';
// CONFIG
import { FlagsContext, launch_darkly_flags } from './flagsContext';
import LaunchDarklyFetch from 'fetch/LaunchDarklyFetch'; // uncomment if using flags in context
import {
  TEN_MINUTES,
  LD_MIEDGE_BROKER_SEARCH_PREMIUM_ALLOCATION_SUMMARY,
  LD_MIEDGE_BROKER_SEARCH_FILTER_CONTACT_ROLE_TITLES,
  LD_MIEDGE_BROKER_SEARCH_BROKER_SHARE,
  LD_MIEDGE_BROKER_SEARCH_FOR_ALL,
} from 'lib/config/appConstants';
import { TableProgressProvider } from 'context/TableProgressContext';

declare module '@mui/styles/defaultTheme' {
  interface DefaultTheme extends Theme {}
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({}),
  defaultOptions: {
    queries: {
      staleTime: TEN_MINUTES,
      cacheTime: Infinity,
    },
  },
});

export const Providers = ({ children }: { children: React.ReactElement }) => {
  const [launchDarklyFlags, setLaunchDarklyFlags] = React.useState(launch_darkly_flags);

  /*
  Launch Darkly Feature toggles

  To include a launch darkly feature toggle, follow the pattern on the commented
  out code below. Each flag is required to have it's own try/catch block. The value
  of the flag is added to the tempFlags object.

  Constants are used to hold the snake case flag name found in Lunch Darkly.

  Once all flags values are retrieved from the proxy server, State is updated with the
  final value of tempFlags which will include all flags needed and current value.

  note: this method will not update the state of the flag unless the user reloads the page/app.
*/
  React.useEffect(() => {
    const controller = new AbortController();
    const getFlag = async () => {
      let tempFlags = { ...launchDarklyFlags };
      /* SAMPLE CODE DO NOT DELETE
      try {
        const miEdgeAllowStatisticsFlag =
          await LaunchDarklyFetch.getLaunchDarklyVariation(MIEDGE_ALLOW_STATISTICS);
        if (!controller.signal.aborted) {
          tempFlags = { ...tempFlags, miEdgeAllowStatistics: miEdgeAllowStatisticsFlag.value };
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          tempFlags = { ...tempFlags, miEdgeAllowStatistics: false };
        }
      }
      */

      try {
        const ldMiedgeBrokerSearchPremiumAllocationSummaryFlag =
          await LaunchDarklyFetch.getLaunchDarklyVariation(
            LD_MIEDGE_BROKER_SEARCH_PREMIUM_ALLOCATION_SUMMARY
          );
        if (!controller.signal.aborted) {
          tempFlags = {
            ...tempFlags,
            ldMiedgeBrokerSearchPremiumAllocationSummary:
              ldMiedgeBrokerSearchPremiumAllocationSummaryFlag.value,
          };
        }
      } catch {
        if (!controller.signal.aborted) {
          tempFlags = { ...tempFlags, ldMiedgeBrokerSearchPremiumAllocationSummary: false };
        }
      }

      // EDP-2250: Add broker share to location summary panels (app.newEdge)
      try {
        const ldMiedgeBrokerSearchBrokerShareFlag =
          await LaunchDarklyFetch.getLaunchDarklyVariation(LD_MIEDGE_BROKER_SEARCH_BROKER_SHARE);
        if (!controller.signal.aborted) {
          tempFlags = {
            ...tempFlags,
            ldMiedgeBrokerSearchBrokerShare: ldMiedgeBrokerSearchBrokerShareFlag.value,
          };
        }
      } catch {
        if (!controller.signal.aborted) {
          tempFlags = { ...tempFlags, ldMiedgeBrokerSearchBrokerShare: false };
        }
      }

      // EDP-2205: Broker Search - Front End Filtering for Contact Job Title/Role
      try {
        const ldMiedgeBrokerSearchFilterContactRoleTitlesFlag =
          await LaunchDarklyFetch.getLaunchDarklyVariation(
            LD_MIEDGE_BROKER_SEARCH_FILTER_CONTACT_ROLE_TITLES
          );
        if (!controller.signal.aborted) {
          tempFlags = {
            ...tempFlags,
            ldMiedgeBrokerSearchFilterContactRoleTitles:
              ldMiedgeBrokerSearchFilterContactRoleTitlesFlag.value,
          };
        }
      } catch {
        if (!controller.signal.aborted) {
          tempFlags = { ...tempFlags, ldMiedgeBrokerSearchFilterContactRoleTitles: false };
        }
      }

      try {
        const ldMiedgeBrokerSearchForAllFlag = await LaunchDarklyFetch.getLaunchDarklyVariation(
          LD_MIEDGE_BROKER_SEARCH_FOR_ALL
        );
        if (!controller.signal.aborted) {
          tempFlags = {
            ...tempFlags,
            ldMiedgeBrokerSearchForAll: ldMiedgeBrokerSearchForAllFlag.value,
          };
        }
      } catch {
        if (!controller.signal.aborted) {
          tempFlags = { ...tempFlags, ldMiedgeBrokerSearchForAll: false };
        }
      }

      // Fetch LaunchDarkly completed
      if (!controller.signal.aborted) {
        tempFlags = { ...tempFlags, isFetching: false };
        setLaunchDarklyFlags(tempFlags);
      }
    };

    getFlag();
    return () => {
      controller.abort();
    };
  }, []);

  return (
    <StrictMode>
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <HelmetProvider>
            <BrowserRouter>
              <FlagsContext.Provider value={launchDarklyFlags}>
                <TableProgressProvider>
                  <StyledEngineProvider injectFirst>
                    <ThemeProvider theme={theme}>{children}</ThemeProvider>
                  </StyledEngineProvider>
                </TableProgressProvider>
              </FlagsContext.Provider>
            </BrowserRouter>
            <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
          </HelmetProvider>
        </QueryClientProvider>
      </Provider>
    </StrictMode>
  );
};
